import { ReactNode, useEffect, useState } from 'react';

import { useMobile } from '../../../../../hooks/useMobile';
import { NAVIGATION_DRAWER_STATE } from '../../../../../../constants/storage';

import { DrawerContext } from './drawerContext';
import { DrawerState, MobileState, DesktopState } from './interfaces';

interface Props {
  children: ReactNode;
}

const DEFAULT_MOBILE_INIT_STATE = 'closed' as MobileState;
const DEFAULT_DESKTOP_INIT_STATE = 'open' as DesktopState;

const getDrawerStateFromLS = (): DrawerState | null => localStorage.getItem(NAVIGATION_DRAWER_STATE) as DrawerState;

const getDrawerState = (isMobile: boolean): DrawerState => {
  if (isMobile) {
    return DEFAULT_MOBILE_INIT_STATE as MobileState;
  }

  const valueFromLS = getDrawerStateFromLS();

  if (!valueFromLS) {
    return DEFAULT_DESKTOP_INIT_STATE as DesktopState;
  }

  if (valueFromLS === 'closed') {
    return 'open' as DesktopState;
  }

  return valueFromLS as DesktopState;
};

export const WithDrawerState = ({ children }: Props) => {
  const { isMobile } = useMobile();
  const [drawerState, setDrawerState] = useState<DrawerState>('initialized');

  const handleState = (newState: DrawerState) => {
    setDrawerState(newState);
    localStorage.setItem(NAVIGATION_DRAWER_STATE, newState);
  };

  const handleOpen = () => {
    handleState('open');
  };

  const handleClose = () => {
    handleState('closed');
  };

  const handleShrink = () => {
    handleState('shrinked');
  };

  const toggleDrawerState = () => {
    if (isMobile) {
      if (drawerState === 'open') handleClose();
      if (drawerState === 'closed') handleOpen();
      if (drawerState === 'shrinked') handleClose();
    } else {
      if (drawerState === 'open') handleShrink();
      if (drawerState === 'shrinked') handleOpen();
      if (drawerState === 'closed') handleShrink();
    }
  };

  useEffect(() => {
    if (isMobile === 'initialized') return;

    handleState(getDrawerState(isMobile));
  }, [isMobile]);

  return <DrawerContext.Provider value={{ drawerState, toggleDrawerState }}>{children}</DrawerContext.Provider>;
};
